import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/Card/Card";
import CompetitorDataForm from "../../components/Forms/CompetitorDataForm";
import useAuth from "../../hooks/useAuth";
import {
  submitApplicationSchema,
  submitBusinessPlanSchema,
  submitBusinessProfileSchema,
  submitExistingBusinessProfileSchema,
} from "../../services/SchemaService";
import ExecutiveSummaryForm from "../../components/Forms/ExecutiveSummaryForm";
import BusinessProfileForm from "../../components/Forms/BusinessProfileForm";
import BusinessIdeaForm from "../../components/Forms/BusinessIdeaForm";
import useApplication from "../../hooks/useApplication";
import {
  Button,
  Checkbox,
  Column,
  Form,
  FormGroup,
  RadioButton,
  RadioButtonGroup,
  Row,
  TextArea,
  TextInput,
} from "carbon-components-react";
import SubmitButton from "../../components/Buttons/SubmitButton";
import CancelButton from "../../components/Buttons/CancelButton";
import { ButtonSet } from "carbon-components-react";
import { Edit24 } from "@carbon/icons-react";
import { SendAlt24 } from "@carbon/icons-react";
import http from "../../http-common";
import { toast } from "react-toastify";
import useGet from "../../hooks/useGet";
import useBusinessPlan from "../../hooks/useBusinessPlan";
import ProvinceCombo from "../../components/Combos/ProvinceCombo";
import DistrictCombo from "../../components/Combos/DistrictCombo";
import GenericCombo from "../../components/Combos/GenericCombo";
import MarketAnalysisForm from "../../components/Forms/MarketAnalysisForm";
import CapacityAnalysisForm from "../../components/Forms/CapacityAnalysisForm";
import WorkersForm from "../../components/Forms/WorkersForm";
import FinancialPlanForm from "../../components/Forms/FinancialPlanForm";
import EnvironmentalSocialAnalysisForm from "../../components/Forms/EnvironmentalSocialAnalysisForm";
import DataSheetForm from "../../components/Forms/DataSheetForm";
import ExistingBusinessProfileForm from "../../components/Forms/ExistingBusinessProfileForm";
import BusinessPlanIdeaForm from "../../components/Forms/BusinessPlanIdeaForm";

const JuryBusinessPlanPage = () => {
  const params = useParams();
  const [uuid] = useState(params.uuid);
  const { businessPlan } = useBusinessPlan(uuid);
  const [hasBusinessCurrently, setHasBusinessCurrently] = useState(null);
  const { data: legalForms } = useGet("/utils/legal-forms");
  const [isOpen, setIsOpen] = useState(false);
  const { user, isCompetitor, isJury } = useAuth();
  const [canScore, setCanScore] = useState(true);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(
      businessPlan.hasBusinessCurrently !== null &&
        businessPlan.hasBusinessCurrently === "Sim"
        ? submitBusinessPlanSchema.concat(submitExistingBusinessProfileSchema)
        : submitBusinessPlanSchema
    ),
  });

  const checkHasScored = async () => {
    try {
      const response = await http.get(
        `/business-plan/evaluation/has-scored?username=${user.username}&uuid=${uuid}`
      );
      setCanScore(response.data);
    } catch (error) {}
  };

  const onSubmit = async (data) => {
    try {
      const response = await http.post("/business-plan/submit/" + uuid);

      toast.success(
        "Plano de Negócio submetido com sucesso. Obrigado e boa sorte!"
      );
      setIsOpen(response.data.currentStatusCode === 1);
      //setIsOpen(false);
    } catch (error) {
      toast.error(error?.response?.data.message);
    }
  };

  useEffect(() => {
    reset(businessPlan);
    // checkHasScored();
    setIsOpen(businessPlan.currentStatusCode === 1);
    setHasBusinessCurrently(businessPlan.hasBusinessCurrently);
  }, [uuid, businessPlan]);

  return (
    <>
      <Card title="Plano de Negócio">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup legendText="1. FICHA TÉCNICA" className="section-header">
            <fieldset fieldset className="bx--fieldset">
              <Row>
                <Column xsm={12} sm={12} md={12} lg={12} className="required">
                  <TextArea
                    id="partnersQualifications"
                    labelText="1.3	Experiência e qualificação dos sócios-gestores/sócias-gestoras"
                    helperText="[Descreva de forma breve, sem destacar o(s) nome(s), o perfil dos/as seus(as) sócios/as, considerando: (i) formação académica/vocacional  e (ii) número de anos de experiência profissional na área para a qual é apresentado este plano de negócio. Use no máximo 100 palavras.]"
                    rows={10}
                    disabled={true}
                    {...register("partnersQualifications")}
                  />
                  <p className="field-error">
                    {errors.partnersQualifications?.message}
                  </p>
                </Column>
              </Row>
            </fieldset>
          </FormGroup>
          <FormGroup
            legendText="3. IDEIA DO NEGÓCIO "
            className="section-header"
          >
            <h6 className="section-subtitle">
              A ser preenchida por todos/as candidatos/as independentemente se
              tem ou não um negócio actualmente
            </h6>
            <fieldset className="bx--fieldset">
              <Row>
                <Column xsm={12} sm={12} md={12} lg={12} className="required">
                  <TextArea
                    id="businessIdea"
                    labelText="3.1	Descrição da Ideia do Negócio"
                    helperText="[Descreva de forma sumária a sua ideia, destacando as principais características inovadoras/diferenciadoras (científicas, tecnológicas, de mercado, do produto/serviço, processo ou modelo de negócio). Entende-se por inovação/diferenciação qualquer aspecto considerado como novo pelo eventual utilizador/mercado, capaz de garantir diferenciação face aos concorrentes. Use no máximo 400 palavras.]"
                    rows={10}
                    enableCounter
                    disabled={true}
                    {...register("businessIdea")}
                  />
                  <p className="field-error">{errors.businessIdea?.message}</p>
                </Column>
              </Row>
            </fieldset>
          </FormGroup>
          <CapacityAnalysisForm
            register={register}
            errors={errors}
            disabled={true}
          />
          <MarketAnalysisForm
            register={register}
            errors={errors}
            disabled={true}
            isJury={isJury}
          />

          <WorkersForm register={register} errors={errors} disabled={true} />
          <FinancialPlanForm
            register={register}
            errors={errors}
            disabled={true}
            businessPlan={businessPlan}
            getValues={getValues}
            isJury={isJury}
          />
          <FormGroup
            legendText="8	ANÁLISE AMBIENTAL E SOCIAL"
            className="section-header"
          >
            <fieldset className="bx--fieldset">
              <Row>
                <Column xsm={12} sm={12} md={12} lg={12} className="required">
                  <TextArea
                    id="risksNegativeEnvironmentalSocialImpacts"
                    labelText="8.3 Com base no tipo de produtos e/ou serviços que pretende fornecer, identifique os possíveis riscos e impactos ambientais negativos e sociais causados pelo seu negócio."
                    rows={7}
                    enableCounter
                    disabled={true}
                    helperText="[Indique os possíveis riscos e impactos ambientais negativos causados pelo negócio bem como as medidas que colocará em prática para minimizar tais riscos e impactos. A sua descrição deve ser consistente com o tipo de actividade indicado na Secção 3. Use no máximo 200 palavras.]"
                    {...register("risksNegativeEnvironmentalSocialImpacts")}
                  />
                  <p className="field-error">
                    {errors.risksNegativeEnvironmentalSocialImpacts?.message}
                  </p>
                </Column>
              </Row>
              <Row>
                <Column xsm={12} sm={12} md={12} lg={12} className="required">
                  <TextArea
                    id="mitigationMeasures"
                    labelText="8.4 Apresente a proposta inicial das medidas de mitigação para os possíveis riscos e impactos identificados."
                    rows={7}
                    enableCounter
                    disabled={true}
                    helperText="[Indique as medidas que colocará em prática para minimizar tais riscos e impactos. Use no máximo 200 palavras.]"
                    {...register("mitigationMeasures")}
                  />
                  <p className="field-error">
                    {errors.mitigationMeasures?.message}
                  </p>
                </Column>
              </Row>
            </fieldset>
          </FormGroup>
          {/* <EnvironmentalSocialAnalysisForm
            register={register}
            errors={errors}
            disabled={true}
          /> */}
          <div className="form-buttons">
            <ButtonSet>
              <Link
                className="bx--btn bx--btn--primary"
                style={{ marginRight: "5px" }}
                to={"../pontuar-plano-de-negocio/" + uuid}
                // target="_blank"
              >
                Pontuar
              </Link>
              <CancelButton navigateTo={""} />
            </ButtonSet>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default JuryBusinessPlanPage;
